import type { ArchitectureDependencies } from '../../application/use-cases/GetArchitectureStatus';
import { DailyMissionsService } from '../../application/services/DailyMissionsService';
import { ChangeDailyMode } from '../../application/use-cases/ChangeDailyMode';
import { ChangeDailyStepStatus } from '../../application/use-cases/ChangeDailyStepStatus';
import { CompleteDailyStep } from '../../application/use-cases/CompleteDailyStep';
import { DeleteAllProfileData } from '../../application/use-cases/DeleteAllProfileData';
import { ExportProfileData } from '../../application/use-cases/ExportProfileData';
import { GetAdaptationSuggestion } from '../../application/use-cases/GetAdaptationSuggestion';
import { GetDailyRoutine } from '../../application/use-cases/GetDailyRoutine';
import { GetOrCreateTodayRoutine } from '../../application/use-cases/GetOrCreateTodayRoutine';
import { GetOrCreateTodayWorkout } from '../../application/use-cases/GetOrCreateTodayWorkout';
import { GetTodayInspiration } from '../../application/use-cases/GetTodayInspiration';
import { ImportProfileData } from '../../application/use-cases/ImportProfileData';
import { InitializeLocalPersistence } from '../../application/use-cases/InitializeLocalPersistence';
import { ListRecentDailyRoutines } from '../../application/use-cases/ListRecentDailyRoutines';
import { PostponeDailyStep } from '../../application/use-cases/PostponeDailyStep';
import { PreviewImportFile } from '../../application/use-cases/PreviewImportFile';
import { ReduceWorkoutSession } from '../../application/use-cases/ReduceWorkoutSession';
import { RespondToAdaptationSuggestion } from '../../application/use-cases/RespondToAdaptationSuggestion';
import { ResetDailyStep } from '../../application/use-cases/ResetDailyStep';
import { ReorderDailySteps } from '../../application/use-cases/ReorderDailySteps';
import { SeedMvpRoutine } from '../../application/use-cases/SeedMvpRoutine';
import { SeedPhilosophyCorpus } from '../../application/use-cases/SeedPhilosophyCorpus';
import { SetEmotionalState } from '../../application/use-cases/SetEmotionalState';
import { SkipDailyStep } from '../../application/use-cases/SkipDailyStep';
import { StartDailyStep } from '../../application/use-cases/StartDailyStep';
import { UpdateExerciseStatus } from '../../application/use-cases/UpdateExerciseStatus';
import { CryptoIdGenerator } from '../../infrastructure/ids/CryptoIdGenerator';
import { BrowserRuntimeCapabilityProvider } from '../../infrastructure/platform/BrowserRuntimeCapabilityProvider';
import { createDexiePersistence } from '../../infrastructure/persistence/createDexiePersistence';
import { SystemClock } from '../../infrastructure/time/SystemClock';
import { SystemLocalDateProvider } from '../../infrastructure/time/SystemLocalDateProvider';

export interface ApplicationDependencies extends ArchitectureDependencies {
  readonly dailyMissions: DailyMissionsService;
}

export function createDependencies(): ApplicationDependencies {
  const persistence = createDexiePersistence();
  const clock = new SystemClock();
  const idGenerator = new CryptoIdGenerator();
  const runtimeCapabilities = new BrowserRuntimeCapabilityProvider();
  const changeStatus = new ChangeDailyStepStatus(
    persistence.dailyRoutines,
    persistence.dailySteps,
    persistence.routineTransactions,
    clock,
    persistence.emotionalProtocolLogs,
  );
  const reorder = new ReorderDailySteps(
    persistence.dailyRoutines,
    persistence.dailySteps,
    persistence.routineStepTemplates,
    persistence.routineTransactions,
    clock,
    persistence.emotionalProtocolLogs,
  );
  const dailyMissions = new DailyMissionsService(
    {
      initialize: new InitializeLocalPersistence(persistence.database, persistence.profiles, clock),
      seed: new SeedMvpRoutine(persistence.routineTransactions, clock, idGenerator),
      seedPhilosophy: new SeedPhilosophyCorpus(persistence.philosophy, clock, idGenerator),
      getToday: new GetOrCreateTodayRoutine(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.routineTemplates,
        persistence.routineStepTemplates,
        persistence.routineTransactions,
        clock,
        new SystemLocalDateProvider(),
        idGenerator,
        persistence.emotionalProtocolLogs,
      ),
      getByDate: new GetDailyRoutine(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.emotionalProtocolLogs,
      ),
      listRecent: new ListRecentDailyRoutines(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.emotionalProtocolLogs,
      ),
      start: new StartDailyStep(changeStatus),
      complete: new CompleteDailyStep(changeStatus),
      reset: new ResetDailyStep(changeStatus),
      skip: new SkipDailyStep(changeStatus),
      postpone: new PostponeDailyStep(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.routineTransactions,
        clock,
        idGenerator,
        persistence.emotionalProtocolLogs,
      ),
      reorder,
      changeMode: new ChangeDailyMode(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.routineStepTemplates,
        persistence.routineTransactions,
        clock,
        idGenerator,
        persistence.emotionalProtocolLogs,
      ),
      setEmotionalState: new SetEmotionalState(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.emotionalProtocolLogs,
        clock,
        idGenerator,
      ),
      getWorkout: new GetOrCreateTodayWorkout(
        persistence.workouts,
        persistence.workoutTransactions,
        clock,
        new SystemLocalDateProvider(),
        idGenerator,
      ),
      updateExercise: new UpdateExerciseStatus(
        persistence.workouts,
        persistence.workoutTransactions,
        clock,
      ),
      reduceWorkout: new ReduceWorkoutSession(
        persistence.workouts,
        persistence.workoutTransactions,
        clock,
      ),
      getInspiration: new GetTodayInspiration(persistence.philosophy, new SystemLocalDateProvider()),
      getAdaptationSuggestion: new GetAdaptationSuggestion(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.appPreferences,
        clock,
      ),
      respondToAdaptationSuggestion: new RespondToAdaptationSuggestion(
        persistence.dailyRoutines,
        persistence.dailySteps,
        persistence.appPreferences,
        reorder,
        clock,
        idGenerator,
      ),
      exportData: new ExportProfileData(persistence.backupTransactions, clock),
      previewImport: new PreviewImportFile(),
      confirmImport: new ImportProfileData(
        persistence.backupTransactions,
        persistence.backups,
        clock,
        idGenerator,
      ),
      deleteAllData: new DeleteAllProfileData(persistence.routineTransactions),
    },
    {
      displayName: 'Yoann',
      locale: 'fr-FR',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    persistence.database,
    runtimeCapabilities,
  );
  return {
    clock,
    idGenerator,
    dailyMissions,
    ...persistence,
  };
}
